import {
  BankOutlined,
  CalendarOutlined,
  CrownOutlined,
  DollarOutlined,
  FileProtectOutlined,
  GroupOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MessageOutlined,
  NotificationOutlined,
  ProfileOutlined,
  SafetyOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import { signOut } from 'aws-amplify/auth'
import cx from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import Logo from '../../../components/Logo/Logo'
import { MenuContext, useMenu } from '../../../components/Menu/context'
import { Role, roleNameMap } from '../../../domain/role.enum'
import { useAppSelector } from '../../../store'
import { feedback } from '../../../lib/sentry'
import { MenuItem } from './menu-item'
import type { Route } from './types'

import '../../../css/Menu.scss'
import { authTokenCache } from '../../../lib/auth-token-cache'

const allRoles: Role[] = [
  Role.Administrator,
  Role.FinanceManager,
  Role.CheckRunManager,
  Role.CheckRun,
  Role.AccountExecutive,
  Role.AccountAdministrator,
  Role.AccountManager,
  Role.CallCenter,
  Role.Claims,
  Role.EPayablesManager
]

export const navigation: Route[] = [
  {
    title: 'Admin',
    icon: <SettingOutlined />,
    children: [
      {
        title: 'Users',
        to: '/admin/users',
        roles: [
          Role.Administrator,
          Role.FinanceManager,
          Role.CheckRunManager,
          Role.AccountAdministrator
        ]
      },
      {
        title: 'Notification Settings',
        to: '/settings/notifications',
        roles: [Role.Administrator, Role.FinanceManager, Role.CheckRun, Role.CheckRunManager]
      },
      {
        title: 'Holidays',
        to: '/holidays',
        roles: [Role.Administrator, Role.CheckRunManager, Role.CheckRun]
      },
      {
        title: 'Healthcheck',
        to: '/healthcheck',
        roles: [Role.Administrator, Role.CheckRunManager, Role.CheckRun]
      },
      {
        title: 'Locations',
        to: '/locations',
        roles: [Role.Administrator, Role.CheckRunManager, Role.CheckRun]
      },
      // {
      //   title: 'Statistics',
      //   to: '/dashboard/statistics',
      //   roles: [Role.Administrator]
      // },
      {
        title: 'Audit Logs',
        to: '/audit-logs',
        roles: [Role.Administrator]
      },
      {
        title: 'Webhook logs',
        to: '/webhook-logs',
        roles: [Role.Administrator]
      },
      {
        title: 'Role Permissions',
        to: '/role-permissions',
        roles: [Role.Administrator]
      },
      {
        title: 'SFTP Diagnostic',
        to: '/diagnostic/sftp',
        roles: [Role.Administrator]
      },
      {
        title: 'SFTP Tree View',
        to: '/admin/sftp-tree-view',
        roles: [Role.Administrator]
      },
      {
        title: 'Temporal Overview',
        to: '/diagnostic/temporal',
        roles: [Role.Administrator]
      },
      {
        title: 'CA Checks',
        to: '/ca/checks',
        roles: [Role.Administrator]
      },
      {
        title: 'CA ePay Check Registers',
        to: '/ca/epay/check-registers',
        roles: [Role.Administrator]
      }
    ]
  },
  {
    title: 'Summary',
    icon: <LineChartOutlined />,
    to: '/dashboard/summary',
    roles: [
      Role.Administrator,
      Role.FinanceManager,
      Role.CheckRunManager,
      Role.CheckRun,
      Role.EPayablesManager
    ]
  },
  {
    title: 'Groups',
    icon: <GroupOutlined />,
    to: '/groups',
    roles: [Role.Administrator, Role.FinanceManager, Role.CheckRunManager]
  },
  {
    title: 'Scheduler',
    icon: <CalendarOutlined />,
    to: '/groups/scheduler',
    roles: [Role.Administrator, Role.CheckRunManager, Role.CheckRun, Role.FinanceManager]
  },
  {
    title: 'Claims',
    icon: <DollarOutlined />,
    to: '/register/claims',
    roles: [
      Role.Administrator,
      Role.FinanceManager,
      Role.CheckRunManager,
      Role.CheckRun,
      Role.CallCenter,
      Role.AccountAdministrator,
      Role.AccountExecutive,
      Role.AccountManager
    ]
  },
  {
    title: 'Check Registers',
    icon: <ProfileOutlined />,
    to: '/register/check-registers',
    roles: [
      Role.Administrator,
      Role.CheckRunManager,
      Role.CheckRun,
      Role.AccountExecutive,
      Role.AccountAdministrator,
      Role.AccountManager,
      Role.EPayablesManager
    ]
  },
  {
    title: 'Release Payment Files',
    icon: <FileProtectOutlined />,
    to: '/register/release-payment-files',
    roles: [Role.Administrator, Role.CheckRunManager, Role.CheckRun, Role.EPayablesManager]
  },
  // {
  //   title: 'Premium Groups',
  //   icon: (<SettingOutlined className='text-zinc-700' />),
  //   to: '/premium/groups',
  //   roles: window.location.hostname === 'payments.lucenthealth.com'
  //     ? [ Role.Administrator ]
  //     : [ Role.Administrator, Role.FinanceManager ]
  // },
  // {
  //   title: 'Premium ACH Pulls',
  //   icon: (<SettingOutlined className='text-zinc-700' />),
  //   to: '/premium/pulls',
  //   roles: window.location.hostname === 'payments.lucenthealth.com'
  //     ? [ Role.Administrator ]
  //     : [ Role.Administrator, Role.FinanceManager ]
  // },
  {
    title: 'Premium',
    icon: <CrownOutlined />,
    roles: [Role.Administrator, Role.FinanceManager],
    children: [
      {
        title: 'Groups',
        to: '/premium/groups',
        roles: [Role.Administrator, Role.FinanceManager]
      },
      {
        title: 'ACH Pulls',
        to: '/premium/pulls',
        matchPaths: ['/premium/pull'],
        roles: [Role.Administrator, Role.FinanceManager]
      },
      {
        title: 'Vendors',
        roles: [Role.Administrator, Role.FinanceManager],
        to: '/premium/vendors'
      }
    ]
  },
  {
    title: 'Pinnacle',
    icon: <BankOutlined />,
    children: [
      {
        title: 'Banking Detail',
        to: '/integrations/pinnacle/feed',
        roles: [Role.Administrator, Role.FinanceManager, Role.EPayablesManager]
      },
      {
        title: 'FID Account Details',
        to: '/integrations/pinnacle/tpa',
        roles: [Role.Administrator, Role.FinanceManager]
      },
      {
        title: 'Lockbox Checks',
        to: '/integrations/pinnacle/lockbox-checks',
        roles: [Role.Administrator, Role.FinanceManager]
      },
      {
        title: 'Lockboxes',
        to: '/integrations/pinnacle/lockboxes',
        roles: [Role.Administrator, Role.FinanceManager]
      },
      {
        title: 'Paid Checks x937',
        to: '/integrations/pinnacle/x937-checks',
        roles: [Role.Administrator, Role.FinanceManager]
      },
      {
        title: 'Transactions',
        to: '/integrations/pinnacle/transactions',
        roles: [Role.Administrator, Role.FinanceManager]
      }
    ]
  },
  {
    title: 'Security',
    icon: <SafetyOutlined />,
    to: '/settings/security',
    roles: allRoles
  },
  {
    title: 'Notifications',
    icon: <NotificationOutlined />,
    to: '/notifications',
    roles: [
      Role.Administrator,
      Role.FinanceManager,
      Role.CheckRunManager,
      Role.AccountAdministrator,
      Role.CheckRun,
      Role.AccountExecutive,
      Role.AccountManager,
      Role.EPayablesManager
    ]
  }
]

export function Menu(): JSX.Element {
  const user = useAppSelector(state => state.app.user)
  const [open, setOpen] = useState(true)

  function handleMenuChange(): void {
    setOpen(prev => !prev)
  }

  const filtered = useMemo(
    () =>
      navigation.reduce<Route[]>((acc, route) => {
        if ('children' in route) {
          const haveAccessToSomeChildren = route.children.some(c =>
            c.roles.some(role => user.roles.includes(role))
          )

          if (!haveAccessToSomeChildren) {
            return acc
          }

          acc.push({
            ...route,
            children: route.children.filter(c => c.roles.some(role => user.roles.includes(role)))
          })

          return acc
        } else {
          if (route.roles.some(r => user.roles.includes(r))) {
            acc.push(route)
          }
          return acc
        }
      }, []),
    [user.roles]
  )

  return (
    <MenuContext.Provider value={{ open, setOpen }}>
      <div
        id='the-sidebar'
        className='flex flex-col justify-between bg-white top-0 sticky h-screen'
      >
        <div
          className={cx(
            'overflow-y-auto overflow-x-hidden relative transition-all duration-300 ease-in flex-1 min-h-0',
            open ? 'w-[280px]' : 'w-[70px]'
          )}
        >
          <div className='flex flex-col gap-4 px-4 py-8'>
            <div className='flex justify-between items-center'>
              <Logo className='pb-2' collapsed={!open} onToggle={handleMenuChange} />
            </div>

            <div className='flex flex-col gap-1.5 overflow-hidden pl-1.5'>
              {filtered.map(route => (
                <MenuItem key={route.title} {...route} />
              ))}
            </div>
          </div>
        </div>

        <User />
      </div>
    </MenuContext.Provider>
  )
}

function User(): JSX.Element {
  const client = useQueryClient()
  const { open: menuOpen } = useMenu()
  const user = useAppSelector(state => state.app.user)

  useEffect(() => {
    feedback.remove()

    let isAttached = false

    const tryAttach = (): void => {
      const element = document.getElementById('sentry-user-feedback')
      if (element && !isAttached) {
        feedback.attachTo(element)
        isAttached = true
      }
    }

    tryAttach()

    const container = document.getElementById('the-sidebar') ?? document.body
    const observer = new MutationObserver(() => {
      tryAttach()
      if (isAttached) observer.disconnect()
    })

    observer.observe(container, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      feedback.remove()
    }
  }, [user])

  async function logout(): Promise<void> {
    client.clear()

    localStorage.setItem('payments_logout_signal', Date.now().toString())
    localStorage.removeItem('token')

    authTokenCache.clearCache()

    await signOut({ global: true })
  }

  function onLogout(): void {
    logout().catch((e: unknown) => {
      console.error('Logout failed:', e)
    })
  }

  return (
    <div
      className={cx(
        'pb-4 transition-all duration-300 ease-in flex-shrink-0',
        menuOpen ? 'w-[280px]' : 'flex flex-col items-center w-[70px]'
      )}
    >
      <div className='mb-2 w-full border-t border-zinc-200' />

      <div className='px-4 mb-2'>
        <button
          id='sentry-user-feedback'
          className={cx(
            'flex flex-row gap-3 items-center text-sm font-normal px-2 py-1.5 whitespace-nowrap transition-colors rounded text-zinc-700 hover:bg-zinc-100 w-full',
            !menuOpen && 'justify-center'
          )}
        >
          <Tooltip title='Give Feedback' placement='right'>
            <MessageOutlined className={cx('text-xl')} />
          </Tooltip>
          {menuOpen && 'Give Feedback'}
        </button>
      </div>

      <div
        className={cx(
          'flex items-center justify-between rounded-lg bg-white px-6 py-4 w-full shadow-none transition-all duration-300 ease-in-out',
          !menuOpen && 'flex-col gap-2 items-center py-2'
        )}
      >
        <div className={cx('flex items-center gap-3', !menuOpen && 'flex-col gap-1')}>
          <Avatar letters={`${user.first_name[0]}${user.last_name[0]}`} />
          {menuOpen && (
            <div className='leading-tight transition-all'>
              <p className='text-sm font-semibold transition-all'>
                {user.first_name} {user.last_name}
              </p>
              <Tooltip title={user.roles.map(r => roleNameMap[r]).join(', ')} placement='top'>
                <p className='text-sm transition-all text-zinc-500'>
                  {user.roles?.[0] && roleNameMap[user.roles?.[0]]}
                </p>
              </Tooltip>
            </div>
          )}
        </div>

        <Tooltip title='Logout' placement='top'>
          <button
            onClick={onLogout}
            className='text-gray-400 transition-colors hover:text-gray-800'
          >
            <LogoutOutlined className={cx('text-xl', !menuOpen && 'text-2xl')} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}

function Avatar({ letters, className }: { letters: string; className?: string }): JSX.Element {
  return (
    <div
      className={cx(
        'flex justify-center items-center rounded-xl size-10 text-[14px] font-base',
        className
      )}
      style={{
        backgroundColor: '#E1F3F9',
        color: '#005F7F'
      }}
    >
      {letters}
    </div>
  )
}

export default Menu
